import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api";
import { snakeToCamel, type FieldDef } from "./fields";
import { QueryTester } from "./QueryTester";
import { RecordForm } from "./RecordForm";
import { VersionsPanel } from "./VersionsPanel";

type Row = Record<string, unknown> & { id: string };
/** A deleted entry as its last version kept it (GET /api/admin/kb/:type/deleted). */
type Deleted = Row & { deleted_at: string; deleted_by: string | null };
type Props = {
  title: string;
  intro: string;
  /** GET lists ({ [rowsKey]: Row[] }), POST creates, PATCH and DELETE take /:id. */
  apiBase: string;
  rowsKey: string;
  listKey: string;
  versionType: string;
  fields: FieldDef[];
  selectOptions?: Record<string, { value: string; label: string }[]>;
  describe: (row: Row) => { primary: string; secondary: string };
};

/**
 * List and editor for small knowledge-base tables (synonyms, intent rules), with history and a query tester.
 * Deleted entries stay listed under "Recently deleted"; choosing one opens its history, where any version can be restored.
 */
export function RowsAdmin(p: Props) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const q = useQuery({ queryKey: [p.listKey], queryFn: () => api<Record<string, Row[]>>(p.apiBase) });
  // Under [listKey] too, so every reload (and a restore, which refreshes everything) updates both lists.
  const del = useQuery({ queryKey: [p.listKey, "deleted"], queryFn: () => api<{ deleted?: Deleted[] }>(`/api/admin/kb/${p.versionType}/deleted`) });
  const rows = q.data?.[p.rowsKey] ?? [];
  const deleted = (del.data?.deleted ?? []).filter((d) => !rows.some((r) => r.id === d.id));
  const cur = rows.find((r) => r.id === selected);
  const gone = cur ? undefined : deleted.find((r) => r.id === selected);
  const reload = () => { for (const k of [p.listKey, "versions", "search", "suggest"]) qc.invalidateQueries({ queryKey: [k] }); };
  // The entry stays selected: once deleted it shows with its history, so it can be restored at once.
  const remove = async (row: Row) => {
    if (!window.confirm("Delete this entry? Search stops using it; you can restore it from its history.")) return;
    try { await api(`${p.apiBase}/${row.id}`, { method: "DELETE" }); setErr(""); reload(); }
    catch (e) { setErr((e as Error).message); }
  };
  const when = (r: Deleted) => `deleted ${new Date(r.deleted_at).toLocaleString()} by ${r.deleted_by ?? "system"}`;

  return (
    <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-2">
      <section className="card">
        <div className="mb-2 flex items-center justify-between">
          <h1 className="text-lg font-semibold">{p.title}</h1>
          <button className="btn-primary" onClick={() => setSelected("new")}>New</button>
        </div>
        <p className="mb-3 text-sm text-slate-500">{p.intro}</p>
        {q.error && <p role="alert" className="mb-2 text-sm text-red-600">{(q.error as Error).message}</p>}
        {q.data && rows.length === 0 && <p className="text-sm text-slate-500">None yet.</p>}
        <ul className="divide-y divide-slate-200 text-sm dark:divide-slate-800">{rows.map((r) => {
          const d = p.describe(r);
          return (
            <li key={r.id}>
              <button className={`w-full px-2 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 ${r.id === selected ? "bg-brand-50" : ""}`}
                onClick={() => setSelected(r.id)}>
                <div className="font-medium">{d.primary}</div>
                <div className="text-xs text-slate-500">{d.secondary}</div>
              </button>
            </li>
          );
        })}</ul>
        {deleted.length > 0 && (
          <>
            <h2 className="mb-1 mt-4 text-sm font-semibold">Recently deleted</h2>
            <p className="mb-2 text-xs text-slate-500">Search no longer uses these. Choose one to restore it from its history.</p>
            <ul aria-label="Recently deleted" className="divide-y divide-slate-200 text-sm dark:divide-slate-800">{deleted.map((r) => {
              const d = p.describe(r);
              return (
                <li key={r.id}>
                  <button className={`w-full px-2 py-1.5 text-left text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 ${r.id === selected ? "bg-brand-50" : ""}`}
                    onClick={() => setSelected(r.id)}>
                    <div className="font-medium line-through">{d.primary}</div>
                    <div className="text-xs">{d.secondary} · {when(r)}</div>
                  </button>
                </li>
              );
            })}</ul>
          </>
        )}
        {del.error && <p role="alert" className="mt-2 text-sm text-red-600">{(del.error as Error).message}</p>}
      </section>
      <div className="flex flex-col gap-4">
        {selected === "new" && (
          <section className="card"><h2 className="mb-3 font-semibold">New</h2>
            <RecordForm key="new" fields={p.fields} sendAll initial={{}} submitLabel="Create" selectOptions={p.selectOptions}
              onSave={async (v) => { const r = await api<{ id: string }>(p.apiBase, { method: "POST", body: v }); reload(); setSelected(r.id); }} />
          </section>
        )}
        {cur && (
          <>
            <section className="card"><h2 className="mb-3 font-semibold">{p.describe(cur).primary}</h2>
              <RecordForm key={cur.id} fields={p.fields} initial={snakeToCamel(cur)} submitLabel="Save" selectOptions={p.selectOptions}
                onSave={async (v) => { await api(`${p.apiBase}/${cur.id}`, { method: "PATCH", body: v }); reload(); }} />
              <button className="btn-ghost mt-3 text-red-600" onClick={() => remove(cur)}>Delete</button>
              {err && <p role="alert" className="mt-2 text-sm text-red-600">{err}</p>}
            </section>
            <section className="card"><h3 className="mb-2 text-sm font-semibold">History</h3>
              <VersionsPanel type={p.versionType} id={cur.id} base="/api/admin/kb" /></section>
          </>
        )}
        {gone && (
          <>
            <section className="card"><h2 className="mb-1 font-semibold">{p.describe(gone).primary}</h2>
              <p className="text-sm text-slate-500">{p.describe(gone).secondary}</p>
              <p role="status" className="mt-2 text-sm text-red-700">Deleted: search no longer uses it ({when(gone)}). Restore a version below to bring it back.</p>
            </section>
            <section className="card"><h3 className="mb-2 text-sm font-semibold">History</h3>
              <VersionsPanel type={p.versionType} id={gone.id} base="/api/admin/kb" deleted /></section>
          </>
        )}
        <QueryTester />
      </div>
    </div>
  );
}
